import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleData } from "@/hooks/useModuleData";
import { usePagedData } from "@/hooks/usePagedData";
import { useSite } from "@/context/SiteContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { num, shortDate } from "@/lib/format";
import api from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import { Truck, Plus, PackageCheck, Hand, Waypoints, Printer, Tag } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function Deliveries() {
  const { data: deliveries, loading, reload } = useModuleData("/deliveries");
  const { items: orders } = usePagedData("/sales-orders", 200);
  const { activeSite } = useSite();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [orderNo, setOrderNo] = useState("");

  if (loading || !deliveries) return <Loader />;

  const created = deliveries.filter((d) => d.status === "CREATED").length;
  const picked = deliveries.filter((d) => d.status === "PICKED").length;
  const issued = deliveries.filter((d) => d.status === "GOODS_ISSUED").length;
  const openOrders = (orders || []).filter((o) => o.status === "OPEN");

  const pick = async (d) => { await api.post(`/deliveries/${d.id}/pick`); toast.success(`${d.delivery_no} picked`); reload(); };
  const gi = async (d) => {
    try { await api.post(`/deliveries/${d.id}/goods-issue`); toast.success(`Goods issue posted · ${d.delivery_no}`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "GI failed"); }
  };
  const create = async () => {
    try { await api.post("/deliveries", { order_no: orderNo }); toast.success("Delivery created"); setOpen(false); setOrderNo(""); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to create delivery"); }
  };
  const buildWave = async () => {
    try {
      const { data } = await api.post("/pick-waves", { site_id: activeSite });
      toast.success(`Pick wave ${data.wave_no} built · ${data.open} tasks`);
      navigate("/picking");
    } catch (e) { toast.error(e?.response?.data?.detail || "No open deliveries to wave"); }
  };

  const columns = [
    { key: "delivery_no", label: "Delivery", render: (r) => <span className="font-mono text-xs text-[#0EA5E9]">{r.delivery_no}</span> },
    { key: "order_no", label: "Order", render: (r) => <span className="font-mono text-[10px] text-[#6a6d70]">{r.order_no}</span> },
    { key: "customer_name", label: "Ship-To / Customer" },
    { key: "material_name", label: "Material", render: (r) => (
      <div><div className="text-[#32363a]">{r.material_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{num(r.qty)} {r.uom}</div></div>) },
    { key: "from_bin", label: "Bin", render: (r) => <span className="font-mono text-xs text-[#6a6d70]">{r.from_bin}</span> },
    { key: "created_at", label: "Created", render: (r) => shortDate(r.created_at) },
    { key: "status", label: "Status", badge: true },
    { key: "actions", label: "", render: (r) => (
      <div className="flex items-center justify-end gap-1">
        <button data-testid={`deliv-dn-${r.delivery_no}`} onClick={() => openPdf(`/print/delivery-note/${r.delivery_no}.pdf`)} title="Print delivery note (A4)"
          className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] px-2 py-1 text-[11px] hover:border-[#0EA5E9]"><Printer size={13} /> DN</button>
        <button data-testid={`deliv-label-${r.delivery_no}`} onClick={() => openPdf(`/print/label/sscc/${r.delivery_no}.pdf`)} title="Print pallet / SSCC label (4x6)"
          className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] px-2 py-1 text-[11px] hover:border-[#0EA5E9]"><Tag size={13} /> Label</button>
        <button data-testid={`deliv-box-${r.delivery_no}`} onClick={() => openPdf(`/print/label/box/${r.delivery_no}.pdf`)} title="Print case / box label (4x6)"
          className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] px-2 py-1 text-[11px] hover:border-[#0EA5E9]"><Tag size={13} /> Box</button>
        {r.status === "CREATED" ? (
          <button data-testid={`deliv-pick-${r.delivery_no}`} onClick={() => pick(r)}
            className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-[#eaedf2] px-2 py-1 text-[11px] hover:border-[#0EA5E9]"><Hand size={13} /> Pick</button>
        ) : r.status === "PICKED" ? (
          <button data-testid={`deliv-gi-${r.delivery_no}`} onClick={() => gi(r)}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-600 hover:border-emerald-500"><PackageCheck size={13} /> Post Goods Issue</button>
        ) : <span className="font-mono text-[10px] text-emerald-600">issued</span>}
      </div>) },
  ];

  return (
    <div>
      <PageHeader title="Outbound Deliveries" subtitle="Create deliveries from sales orders, bundle them into pick waves, pick against bins, and post goods issue." icon={Truck} accent="#0EA5E9">
        <div className="flex items-center gap-2">
          <button data-testid="build-wave-btn" onClick={buildWave}
            className="inline-flex items-center gap-2 rounded-lg border border-[#0EA5E9] bg-white px-4 py-2 text-sm font-semibold text-[#0284c7] hover:bg-[#f0f9ff]"><Waypoints size={16} /> Build Pick Wave</button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="create-delivery-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#0EA5E9] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0284c7]"><Plus size={16} /> Create Delivery</button>
            </DialogTrigger>
            <DialogContent className="border-[#e5e9f0] bg-white text-[#32363a]">
              <DialogHeader><DialogTitle className="uppercase">Create Outbound Delivery</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <select data-testid="delivery-order-select" value={orderNo} onChange={(e) => setOrderNo(e.target.value)} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
                  <option value="">Select open sales order…</option>
                  {openOrders.map((o) => <option key={o.id} value={o.order_no}>{o.order_no} · {o.customer_name} · {num(o.qty)} {o.uom}</option>)}
                </select>
              </div>
              <DialogFooter>
                <button data-testid="delivery-create-submit" onClick={create} disabled={!orderNo}
                  className="rounded-lg bg-[#0EA5E9] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Create</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Deliveries" value={deliveries.length} accent="#0EA5E9" />
        <KpiCard label="To Pick" value={created} accent="#F59E0B" />
        <KpiCard label="Picked" value={picked} accent="#8B5CF6" />
        <KpiCard label="Goods Issued" value={issued} accent="#10B981" />
      </div>

      <DataTable testId="deliveries-table" columns={columns} rows={deliveries} empty="No deliveries yet — create one from an open sales order." />
    </div>
  );
}
